import cx from 'classnames';
import { motion } from 'framer-motion';
import { Link } from 'gatsby';
import React, { forwardRef } from 'react';

import * as styles from './Button.module.css';

import Arrow from '@components/SVG/Arrow';
import ArrowDown from '@components/SVG/ArrowDown';
import ArrowLeft from '@components/SVG/ArrowLeft';
import Configure from '@components/SVG/Configure';

const Button = ({
	children,
	to,
	variant = 'standard',
	type = 'button',
	size = 'md',
	theme = 'standard',
	align = 'start',
	className = '',
	onClick,
	...rest
}: {
	children: React.ReactNode;
	to?: string;
	variant?: 'standard' | 'back' | 'download' | 'configure';
	type?: 'button' | 'submit' | 'reset';
	size?: 'sm' | 'md' | 'lg';
	theme?: 'standard' | 'ghost' | 'invert';
	align?: 'start' | 'center' | 'end' | 'stretch';
	onClick?: any;
	className?: string;
}): JSX.Element => {
	const componentClassNames = cx(styles.button, {
		[className]: !!className,
		[styles[`${size}Size`]]: !!size,
		[styles[`${theme}Theme`]]: !!theme,
		[styles[`${align}Align`]]: align,
	});

	const isInternalLink = /^\/(?!\/)/.test(to);

	const AnimatedLink = motion(
		forwardRef((props, ref) => <Link {...props} ref={ref}></Link>)
	);

	return to ? (
		isInternalLink ? (
			<AnimatedLink
				to={to}
				download={variant === 'download'}
				className={componentClassNames}
				onClick={onClick}
				{...rest}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true, amount: 0.6 }}
				transition={{ duration: 0.35 }}
				variants={{
					visible: { opacity: 1 },
					hidden: { opacity: 0 },
				}}>
				{variant === 'back' && (
					<ArrowLeft className={styles.iconArrow} />
				)}

				{children}

				{variant === 'download' && (
					<ArrowDown
						className={cx(styles.iconArrow, styles.iconArrowDown)}
					/>
				)}

				{variant === 'standard' && (
					<Arrow className={styles.iconArrow} />
				)}
			</AnimatedLink>
		) : (
			<motion.a
				href={to}
				download={variant === 'download'}
				className={componentClassNames}
				target={variant !== 'configure' ? '_blank' : ''}
				rel={variant !== 'configure' ? 'nofollow noopener' : ''}
				onClick={onClick}
				{...rest}
				initial="hidden"
				whileInView="visible"
				viewport={{ once: true, amount: 0.6 }}
				transition={{ duration: 0.35 }}
				variants={{
					visible: { opacity: 1 },
					hidden: { opacity: 0 },
				}}>
				{variant === 'back' && (
					<ArrowLeft className={styles.iconArrow} />
				)}
				{children}
				{variant === 'download' && (
					<ArrowDown
						className={cx(styles.iconArrow, styles.iconArrowDown)}
					/>
				)}
				{variant === 'standard' && (
					<Arrow className={styles.iconArrow} />
				)}

				{variant === 'configure' && (
					<Configure className={styles.iconConfigure} />
				)}
			</motion.a>
		)
	) : (
		<motion.button
			className={componentClassNames}
			type={type}
			onClick={onClick}
			{...rest}
			initial="hidden"
			whileInView="visible"
			viewport={{ once: true, amount: 0.6 }}
			transition={{ duration: 0.35 }}
			variants={{
				visible: { opacity: 1 },
				hidden: { opacity: 0 },
			}}>
			{children}

			{variant === 'configure' && (
				<Configure className={styles.iconConfigure} />
			)}
		</motion.button>
	);
};

export default Button;
